import React, { useCallback, useEffect, useMemo, useState } from "react";
import { createPortal } from "react-dom";
import {
  BrowserRouter,
  Navigate,
  Route,
  Routes,
  useLocation,
} from "react-router-dom";
import AppLayoutToolbar from "@amzn/awsui-components-react/polaris/app-layout-toolbar";
import SpaceBetween from "@amzn/awsui-components-react/polaris/space-between";
import { useSelector } from "react-redux";

import { DEFAULT_LEFT_NAV_PATH } from "./shellRoutes";
import { LeftNav } from "./LeftNav";
import { AppBreadcrumbs } from "./AppBreadcrumbs";
import { FilterBar } from "../filterBar/FilterBar";
import { PorAsinListTable } from "../porAsinList/PorAsinListTable";
import { BaselineTable } from "../dateBaseline/baselineTable";
import { ValidationDashboardPage } from "../validation/ValidationDashboardPage";
import { OneClickLaunchTable } from "../oneClickLaunch/oneClickLaunchTable";
import { StamperWizard } from "../stampUtils/StamperWizard";
import { LoomAgentAssistantWidget } from "../../components/loom-agent-assistant/LoomAgentAssistantWidget";
import { selectIsLoomAgentAssistantEnabled } from "../userInfo/userInfoSlice";
import { InitializeRum } from "../rum/initializeRum";
import { Notification } from "../notification/notification";
import { CacheableNotification } from "../notification/cacheableNotification";
import { StaleDataAlert } from "../validation/StaleDataAlert";
import { V2Toggle } from "../app/V2Toggle";

const AGENT_ASSISTANT_DRAWER_ID = "agent-assistant";

const AGENT_ASSISTANT_DRAWER_STYLE: React.CSSProperties = {
  height: "100%",
  width: "100%",
  boxSizing: "border-box",
};

// Cloudscape's drawer-triggers container has a hashed scoped class.
const CLOUDSCAPE_DRAWERS_SELECTOR = '[class*="universal-toolbar-drawers"]';

/**
 * Portals children next to Cloudscape's drawer triggers so they
 * inherit the toolbar's sticky/rubber-band behavior. Uses a dedicated
 * host `<div>` (opaque to Cloudscape's React tree) prepended into the
 * container. If the container isn't in the DOM at mount (Cloudscape's
 * toolbar loads via widgetization), we watch briefly with a
 * `MutationObserver` and disconnect once it appears.
 */
const ToolbarPortal: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const [host, setHost] = useState<HTMLDivElement | null>(null);

  useEffect(() => {
    const ownedHost = document.createElement("div");
    ownedHost.dataset.testid = "toolbar-portal-host";
    ownedHost.style.display = "flex";
    ownedHost.style.alignItems = "center";
    // Prevent the flex parent from shrinking our text button to
    // icon-trigger width and wrapping the label.
    ownedHost.style.flexShrink = "0";
    ownedHost.style.whiteSpace = "nowrap";

    // Track the Cloudscape element we mutated + its prior style so we
    // can restore it on unmount instead of leaving state behind on a
    // parent we don't own.
    let styledContainer: HTMLElement | null = null;
    let prevJustifySelf = "";

    // Attach if the container is in the DOM. Returns true when the
    // container exists (whether or not we needed to re-attach).
    const findAndAttach = (): boolean => {
      const container = document.querySelector(CLOUDSCAPE_DRAWERS_SELECTOR);
      if (!container) {
        return false;
      }
      if (ownedHost.parentElement !== container) {
        container.prepend(ownedHost);
        setHost(ownedHost);
      }
      // `justify-self: end` on the drawers grid item pulls the
      // [toggle, AI trigger] cluster tight against the right edge.
      // Re-apply on every attach so a re-mounted container also
      // gets it; restore the previous container's style first so we
      // don't leave a stale mutation on a detached node.
      if (container instanceof HTMLElement && styledContainer !== container) {
        if (styledContainer !== null) {
          styledContainer.style.justifySelf = prevJustifySelf;
        }
        styledContainer = container;
        prevJustifySelf = container.style.justifySelf;
        container.style.justifySelf = "end";
      }
      return true;
    };

    // Observe the whole body subtree for the app's lifetime so we
    // can re-attach if Cloudscape ever re-creates the drawers
    // container (`isLoomAgentAssistantEnabled` resolves async, and
    // route/`contentType` changes can prompt Cloudscape to re-render
    // the toolbar). The callback is on a hot path in table-heavy
    // views, so early-return the common case where we're still
    // attached — the real work only runs when the host is detached.
    findAndAttach();
    const observer = new MutationObserver(() => {
      if (document.contains(ownedHost)) {
        return;
      }
      findAndAttach();
    });
    observer.observe(document.body, { childList: true, subtree: true });

    return () => {
      observer.disconnect();
      if (styledContainer !== null) {
        styledContainer.style.justifySelf = prevJustifySelf;
      }
      ownedHost.remove();
    };
  }, []);

  if (host === null) {
    return null;
  }
  return createPortal(children, host);
};

/**
 * App Shell root. FilterBar mounts above `<Routes/>` so it survives
 * intra-shell nav without remount. Drawer config mirrors Legacy.
 */
export const AppShell: React.FC = () => {
  const isLoomAgentAssistantEnabled = useSelector(
    selectIsLoomAgentAssistantEnabled
  );

  const [activeDrawerId, setActiveDrawerId] = useState<string | null>(null);
  const handleDrawerChange = useCallback(
    ({ detail }: { detail: { activeDrawerId: string | null } }) => {
      setActiveDrawerId(detail.activeDrawerId);
    },
    []
  );

  const drawers = useMemo(
    () =>
      isLoomAgentAssistantEnabled
        ? [
            {
              id: AGENT_ASSISTANT_DRAWER_ID,
              ariaLabels: {
                drawerName: "Loom Agent Assistant",
                closeButton: "Close Loom Agent Assistant",
                triggerButton: "Open Loom Agent Assistant",
                resizeHandle: "Resize Loom Agent Assistant panel",
              },
              trigger: { iconName: "gen-ai" as const },
              resizable: true,
              defaultSize: 400,
              content: (
                <div
                  data-testid="agent-assistant-drawer"
                  style={AGENT_ASSISTANT_DRAWER_STYLE}
                >
                  <LoomAgentAssistantWidget />
                </div>
              ),
            },
          ]
        : [],
    [isLoomAgentAssistantEnabled]
  );

  return (
    <BrowserRouter>
      {/* Portal the toggle next to the AI-drawer trigger so it shares
          the toolbar's sticky + rubber-band behavior. */}
      <ToolbarPortal>
        <V2Toggle />
      </ToolbarPortal>
      <AppLayoutToolbar
        data-testid="app-shell-layout"
        // Match Legacy: offset the sticky toolbar below the mosaic
        // strip so it doesn't hide behind it on scroll.
        headerSelector={"#mosaic-navigation"}
        footerSelector={"#footer"}
        navigation={<LeftNav />}
        breadcrumbs={<AppBreadcrumbs />}
        // `stickyNotifications` pins this slot below the toolbar, so
        // the FilterBar inherits Cloudscape's offset math.
        notifications={<AppShellNotifications />}
        stickyNotifications={true}
        contentType="table"
        toolsHide={true}
        activeDrawerId={activeDrawerId}
        onDrawerChange={handleDrawerChange}
        drawers={drawers}
        content={<AppShellRoutedContent />}
      />
    </BrowserRouter>
  );
};

// FilterBar drives ASIN-list filtering; Stamper Tool is a distinct
// workflow that doesn't consume that state, so we skip the bar on
// `/stamper` to avoid a stale filter UI above an unrelated wizard.
const PATHS_WITHOUT_FILTER_BAR: ReadonlySet<string> = new Set(["/stamper"]);

/** Sticky-notifications slot: FilterBar (where applicable) + banners. */
const AppShellNotifications: React.FC = () => {
  const location = useLocation();
  const showFilterBar = !PATHS_WITHOUT_FILTER_BAR.has(location.pathname);

  return (
    <SpaceBetween size="s" direction="vertical">
      {showFilterBar && <FilterBar />}
      <div data-testid="notifications-container">
        <Notification />
        <CacheableNotification />
        <StaleDataAlert />
      </div>
    </SpaceBetween>
  );
};

const AppShellRoutedContent: React.FC = () => {
  return (
    <div data-testid="app-shell-content">
      <InitializeRum />
      <Routes>
        <Route
          path="/"
          element={<Navigate to={DEFAULT_LEFT_NAV_PATH} replace />}
        />
        <Route path="/search" element={<PorAsinListTable />} />
        <Route path="/baseline" element={<BaselineTable />} />
        <Route path="/validation" element={<ValidationDashboardPage />} />
        <Route path="/launch" element={<OneClickLaunchTable />} />
        <Route path="/stamper" element={<StamperWizard />} />
        <Route
          path="*"
          element={<Navigate to={DEFAULT_LEFT_NAV_PATH} replace />}
        />
      </Routes>
    </div>
  );
};

export default AppShell;
